import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Form, FormGroup, Label, Input, Row, Col, InputGroup, InputGroupText, Alert } from 'reactstrap';
import { toast } from 'react-toastify';
import { handleAddModifyOperation } from '../../../../Api/ApiMethods';

const ExpertWitnessReportDataModal = ({
    apiEndpoint,
    isOpen,
    toggle,
    mode,
    selectedExpertWitnessReport,
    refreshData,
    arbitrationId
}) => {
    const [expertWitnessReportData, setExpertWitnessReportData] = useState({
        arbitration: [],
        arrivalDate: '',
        lastDay: '',
        reportType: '',
        ewDefectDeterminationFee: '',
        ewClaimDeterminationFee: '',
        actionForReport: '',
        amendmentFee: '',
        comments: ''
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    useEffect(() => {
        if (isOpen) {
            if (mode === 'add') {
                setExpertWitnessReportData({
                    arbitration: [arbitrationId], // arbitrationId'yi array olarak set et
                    arrivalDate: '',
                    lastDay: '',
                    reportType: '',
                    ewDefectDeterminationFee: '',
                    ewClaimDeterminationFee: '',
                    actionForReport: '',
                    amendmentFee: '',
                    comments: ''
                });
            } else if (mode === 'modify' && selectedExpertWitnessReport) {
                // API'den gelen veri formatını forma uygun hale getir
                const commentText = selectedExpertWitnessReport.comments && Array.isArray(selectedExpertWitnessReport.comments) && selectedExpertWitnessReport.comments.length > 0 
                    ? selectedExpertWitnessReport.comments[0].comment || ''
                    : '';

                setExpertWitnessReportData({
                    id: selectedExpertWitnessReport.id,
                    arbitration: selectedExpertWitnessReport.arbitration || [arbitrationId],
                    arrivalDate: selectedExpertWitnessReport.arrivalDate ? selectedExpertWitnessReport.arrivalDate.split('T')[0] : '',
                    lastDay: selectedExpertWitnessReport.lastDay ? selectedExpertWitnessReport.lastDay.split('T')[0] : '',
                    reportType: selectedExpertWitnessReport.reportType || '',
                    ewDefectDeterminationFee: selectedExpertWitnessReport.ewDefectDeterminationFee?.toString() || '',
                    ewClaimDeterminationFee: selectedExpertWitnessReport.ewClaimDeterminationFee?.toString() || '',
                    actionForReport: selectedExpertWitnessReport.actionForReport || '',
                    amendmentFee: selectedExpertWitnessReport.amendmentFee?.toString() || '',
                    comments: commentText
                });
            }
            setValidationErrors({});
        }
    }, [isOpen, mode, selectedExpertWitnessReport, arbitrationId]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // Kusur Tespit alanı için yüzde kontrolü (0-100 arası tam sayı)
        if (name === 'ewDefectDeterminationFee') {
            // Sadece rakam karakterlerini kabul et (nokta ve virgül yok)
            let processedValue = value.replace(/[^0-9]/g, '');
            
            // Boş string ise direkt set et
            if (processedValue === '') {
                setExpertWitnessReportData(prev => ({ ...prev, [name]: processedValue }));
            } else {
                // Sayıya çevir ve 0-100 arasında kontrol et
                const numValue = parseInt(processedValue);
                if (numValue >= 0 && numValue <= 100) {
                    setExpertWitnessReportData(prev => ({ ...prev, [name]: processedValue }));
                }
                // 100'den büyükse işlem yapma (eski değer kalır)
            }
        }
        // Diğer para alanları için (Talep Tespit ve Islah Ücreti) ondalık kabul et
        else if (name === 'ewClaimDeterminationFee' || name === 'amendmentFee') {
            // Sadece rakam, nokta ve virgül karakterlerini kabul et
            let processedValue = value.replace(/[^0-9.,]/g, '');
            processedValue = processedValue.replace(',', '.');
            const decimalCheck = processedValue.split('.');
            if (decimalCheck.length > 2) {
                processedValue = decimalCheck[0] + '.' + decimalCheck.slice(1).join('');
            }
            if (decimalCheck[1] && decimalCheck[1].length > 2) {
                processedValue = decimalCheck[0] + '.' + decimalCheck[1].slice(0, 2);
            }
            setExpertWitnessReportData(prev => ({ ...prev, [name]: processedValue }));
        } else {
            setExpertWitnessReportData(prev => ({
                ...prev,
                [name]: value
            }));
        }
        
        // Clear validation error when user starts typing
        if (validationErrors[name]) {
            setValidationErrors(prev => ({
                ...prev,
                [name]: null
            }));
        }
    };

    const validateForm = () => {
        const errors = {};
        
        if (!expertWitnessReportData.reportType?.trim()) {
            errors.reportType = 'Rapor türü gereklidir';
        }
        
        if (!expertWitnessReportData.arrivalDate) {
            errors.arrivalDate = 'Geliş tarihi gereklidir';
        }

        // Kusur Tespit (Yüzde) alanı validasyonu
        if (expertWitnessReportData.ewDefectDeterminationFee !== '' && expertWitnessReportData.ewDefectDeterminationFee !== null) {
            const defectValue = parseInt(expertWitnessReportData.ewDefectDeterminationFee);
            if (isNaN(defectValue) || defectValue < 0 || defectValue > 100) {
                errors.ewDefectDeterminationFee = 'Kusur tespit yüzdesi 0-100 arasında olmalıdır';
            }
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            toast.error('Lütfen zorunlu alanları doldurun.');
            return;
        }

        setIsSubmitting(true);
        
        try {
            // API'ye gönderilecek veriyi API yapısına uygun şekilde hazırla
            const apiData = {
                ...expertWitnessReportData,
                arbitration: [arbitrationId], // arbitrationId'yi array olarak gönder
                // Sayısal alanları InterlocutoryRuling'deki mantıkla number'a çevir
                ewDefectDeterminationFee: expertWitnessReportData.ewDefectDeterminationFee ? Number(String(expertWitnessReportData.ewDefectDeterminationFee).replace(/,/g, '.')) || 0 : 0,
                ewClaimDeterminationFee: expertWitnessReportData.ewClaimDeterminationFee ? Number(String(expertWitnessReportData.ewClaimDeterminationFee).replace(/,/g, '.')) || 0 : 0,
                amendmentFee: expertWitnessReportData.amendmentFee ? Number(String(expertWitnessReportData.amendmentFee).replace(/,/g, '.')) || 0 : 0,
                // Comments'i API'nin beklediği formata çevir
                comments: expertWitnessReportData.comments ? [{ comment: expertWitnessReportData.comments }] : []
            };

            console.log('ExpertWitnessReportDataModal: API Data being sent:', apiData);

            const result = await handleAddModifyOperation({
                apiEndpoint,
                selectedItem: mode === 'modify' ? selectedExpertWitnessReport : null,
                mode,
                data: apiData,
                refreshDataCallback: refreshData,
                componentName: 'Bilirkişi raporu'
            });

            toggle();
            refreshData();
        } catch (error) {
            console.error('ExpertWitnessReportDataModal: Submit error:', error);
            toast.error('Bir hata oluştu. Lütfen tekrar deneyin.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>
                {mode === 'add' ? 'Bilirkişi Raporu Ekle' : 'Bilirkişi Raporu Düzenle'}
            </ModalHeader>
            <Form onSubmit={handleSubmit}>
                <ModalBody>
                    {Object.keys(validationErrors).length > 0 && (
                        <Alert color="danger">
                            <ul className="mb-0">
                                {Object.values(validationErrors).map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        </Alert>
                    )}

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label for="arrivalDate">
                                    Geliş Tarihi <span className="text-danger">*</span>
                                </Label>
                                <Input
                                    type="date"
                                    name="arrivalDate"
                                    id="arrivalDate"
                                    value={expertWitnessReportData.arrivalDate}
                                    onChange={handleInputChange}
                                    invalid={!!validationErrors.arrivalDate}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label for="lastDay">Son Gün</Label>
                                <Input
                                    type="date"
                                    name="lastDay"
                                    id="lastDay"
                                    value={expertWitnessReportData.lastDay}
                                    onChange={handleInputChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label for="reportType">
                                    Rapor Türü <span className="text-danger">*</span>
                                </Label>
                                <Input
                                    type="select"
                                    name="reportType"
                                    id="reportType"
                                    value={expertWitnessReportData.reportType}
                                    onChange={handleInputChange}
                                    invalid={!!validationErrors.reportType}
                                >
                                    <option value="">Rapor türü seçiniz</option>
                                    <option value="Değer Kaybı">Değer Kaybı</option>
                                    <option value="Hasar Bedeli">Hasar Bedeli</option>
                                    <option value="Hasar Farkı">Hasar Farkı</option>
                                    <option value="Değer Kaybı Kusur">Değer Kaybı Kusur</option>
                                    <option value="Hasar Farkı Kusur">Hasar Farkı Kusur</option>
                                    <option value="Ek Rapor">Ek Rapor</option>
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label for="ewDefectDeterminationFee">Kusur Tespit</Label>
                                <InputGroup>
                                    <Input
                                        type="text"
                                        name="ewDefectDeterminationFee"
                                        id="ewDefectDeterminationFee"
                                        value={expertWitnessReportData.ewDefectDeterminationFee}
                                        onChange={handleInputChange}
                                        placeholder="0-100"
                                        invalid={!!validationErrors.ewDefectDeterminationFee}
                                        style={{ 
                                            textAlign: 'right',
                                            borderTopRightRadius: 0,
                                            borderBottomRightRadius: 0
                                        }}
                                    />
                                    <InputGroupText 
                                        style={{ 
                                            borderTopLeftRadius: 0,
                                            borderBottomLeftRadius: 0,
                                            backgroundColor: '#f8f9fa',
                                            borderLeft: 'none'
                                        }}
                                    >
                                        %
                                    </InputGroupText>
                                </InputGroup>
                                {validationErrors.ewDefectDeterminationFee && (
                                    <small className="text-danger">{validationErrors.ewDefectDeterminationFee}</small>
                                )}
                                <small className="text-muted">0-100 arası tam sayı giriniz</small>
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label for="ewClaimDeterminationFee">Talep Tespit</Label>
                                <InputGroup>
                                    <Input
                                        type="text"
                                        name="ewClaimDeterminationFee"
                                        id="ewClaimDeterminationFee"
                                        value={expertWitnessReportData.ewClaimDeterminationFee}
                                        onChange={handleInputChange}
                                        placeholder="Örn: 5000.75"
                                        style={{ 
                                            textAlign: 'right',
                                            borderTopRightRadius: 0,
                                            borderBottomRightRadius: 0
                                        }}
                                    />
                                    <InputGroupText 
                                        style={{ 
                                            borderTopLeftRadius: 0,
                                            borderBottomLeftRadius: 0,
                                            backgroundColor: '#f8f9fa',
                                            borderLeft: 'none'
                                        }}
                                    >
                                        ₺
                                    </InputGroupText>
                                </InputGroup>
                                <small className="text-muted">Ondalık ayırıcı olarak nokta (.) kullanınız</small>
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label for="amendmentFee">Islah Ücreti</Label>
                                <InputGroup>
                                    <Input
                                        type="text"
                                        name="amendmentFee"
                                        id="amendmentFee"
                                        value={expertWitnessReportData.amendmentFee}
                                        onChange={handleInputChange}
                                        placeholder="Örn: 5000.75"
                                        style={{ 
                                            textAlign: 'right',
                                            borderTopRightRadius: 0,
                                            borderBottomRightRadius: 0
                                        }}
                                    />
                                    <InputGroupText 
                                        style={{ 
                                            borderTopLeftRadius: 0,
                                            borderBottomLeftRadius: 0,
                                            backgroundColor: '#f8f9fa',
                                            borderLeft: 'none'
                                        }}
                                    >
                                        ₺
                                    </InputGroupText>
                                </InputGroup>
                                <small className="text-muted">Ondalık ayırıcı olarak nokta (.) kullanınız</small>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label for="actionForReport">Rapor İçin Eylem</Label>
                                <Input
                                    type="select"
                                    name="actionForReport"
                                    id="actionForReport"
                                    value={expertWitnessReportData.actionForReport}
                                    onChange={handleInputChange}
                                >
                                    <option value="">Eylem seçiniz</option>
                                    <option value="Beyan">Beyan</option>
                                    <option value="Islah">Islah</option>
                                    <option value="İtiraz">İtiraz</option>
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label for="comments">Yorum</Label>
                                <Input
                                    type="textarea"
                                    name="comments"
                                    id="comments"
                                    rows="3"
                                    value={expertWitnessReportData.comments}
                                    onChange={handleInputChange}
                                    placeholder="Yorumlarınızı giriniz..."
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Button 
                        color="secondary" 
                        onClick={toggle} 
                        disabled={isSubmitting}
                    >
                        İptal
                    </Button>
                    <Button 
                        color="primary" 
                        type="submit" 
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Kaydediliyor...' : (mode === 'add' ? 'Ekle' : 'Güncelle')}
                    </Button>
                </ModalFooter>
            </Form>
        </Modal>
    );
};

export default ExpertWitnessReportDataModal;
